import { useRef, useState, useCallback, useEffect } from "react";
import {
  Upload, X, Wand2, Copy, Check, RefreshCw, Download, Save,
  ImagePlus, Loader2, AlertCircle, RotateCcw, Tag, ClipboardCopy,
  Camera, Sparkles, LayoutList, Pencil, Trash2, ChevronLeft,
  ChevronRight, PackageSearch,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../ui/alert-dialog";
import { api, getErrorMessage } from "../../../lib/api";

const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];
const PAGE_SIZE = 12;

// ── helpers ───────────────────────────────────────────────────────────────────
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const fmt = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

function useCopy() {
  const [copiedId, setCopiedId] = useState(null);
  const copy = (text, id = "default") => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 2000);
    });
  };
  return { copy, isCopied: (id) => copiedId === id };
}

// ── typewriter / streaming animation ─────────────────────────────────────────
async function animateFields(full, onUpdate, signal) {
  const d = {};
  const aborted = () => signal?.aborted;

  // type a string character by character
  const typeText = async (key, text, ms = 15) => {
    d[key] = "";
    onUpdate({ ...d });
    for (let i = 1; i <= (text || "").length; i++) {
      if (aborted()) return;
      d[key] = text.slice(0, i);
      onUpdate({ ...d });
      await sleep(ms);
    }
  };

  await typeText("meesho_title", full.meesho_title || "", 15);
  if (aborted()) return;
  await typeText("flipkart_title", full.flipkart_title || "", 15);
  if (aborted()) return;

  // description word by word (split preserving whitespace)
  const chunks = (full.description || "").split(/(\s+)/);
  d.description = "";
  for (const chunk of chunks) {
    if (aborted()) return;
    d.description += chunk;
    onUpdate({ ...d });
    await sleep(8);
  }

  // features one at a time
  d.features = [];
  for (const feat of (full.features || [])) {
    if (aborted()) return;
    d.features = [...d.features, feat];
    onUpdate({ ...d });
    await sleep(120);
  }

  // attributes all at once
  if (aborted()) return;
  for (const k of ["material", "color", "pattern", "occasion", "category", "subcategory", "package_contents"]) {
    d[k] = full[k] ?? null;
  }
  onUpdate({ ...d });
  await sleep(200);

  // SEO keywords in batches of 5
  d.seo_keywords = [];
  const kws = full.seo_keywords || [];
  for (let i = 0; i < kws.length; i += 5) {
    if (aborted()) return;
    d.seo_keywords = [...d.seo_keywords, ...kws.slice(i, i + 5)];
    onUpdate({ ...d });
    await sleep(70);
  }

  // search tags one at a time
  d.search_tags = [];
  for (const tag of (full.search_tags || [])) {
    if (aborted()) return;
    d.search_tags = [...d.search_tags, tag];
    onUpdate({ ...d });
    await sleep(60);
  }

  // generated images (appear at end)
  if (!aborted()) {
    d.generated_images = full.generated_images || [];
    onUpdate({ ...d });
  }
}

// ── shared atoms ──────────────────────────────────────────────────────────────
const Bone = ({ className = "" }) => (
  <div className={`animate-pulse bg-purple-50 rounded-xl ${className}`} />
);

const ListingSkeleton = ({ showImageSkeleton = false }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50/60">
      <Bone className="h-5 w-52" />
    </div>
    <div className="p-5 space-y-6">
      {showImageSkeleton && (
        <>
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map((i) => <Bone key={i} className="aspect-square rounded-xl" />)}
          </div>
          <div className="border-t border-slate-100" />
        </>
      )}
      {[1, 2].map((i) => (
        <div key={i} className="space-y-2"><Bone className="h-3 w-28" /><Bone className="h-11 w-full" /></div>
      ))}
      <div className="space-y-2"><Bone className="h-3 w-24" /><Bone className="h-32 w-full" /></div>
      <div className="space-y-2">{[1, 2, 3, 4].map((i) => <Bone key={i} className="h-9 w-full" />)}</div>
      <div className="grid grid-cols-2 gap-3">{[1, 2, 3, 4, 5, 6].map((i) => <Bone key={i} className="h-14" />)}</div>
      <div className="flex flex-wrap gap-2">
        {["w-20", "w-28", "w-24", "w-16", "w-32", "w-20"].map((w, i) => (
          <Bone key={i} className={`h-7 ${w}`} />
        ))}
      </div>
    </div>
  </div>
);

const CopyBtn = ({ text, id, copy, isCopied }) => (
  <button
    onClick={() => copy(text, id)}
    className="flex items-center gap-1.5 text-xs font-medium text-purple-700 hover:text-purple-900 px-2 py-1 rounded-xl hover:bg-purple-50 transition-colors"
  >
    {isCopied(id) ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
    {isCopied(id) ? "Copied" : "Copy"}
  </button>
);

const RegenBtn = ({ onClick, loading, label }) => (
  <button
    onClick={onClick}
    disabled={loading}
    className="flex items-center gap-1.5 text-xs font-medium text-violet-600 hover:text-violet-800 px-2 py-1 rounded-xl hover:bg-violet-50 transition-colors disabled:opacity-50"
  >
    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
    {label}
  </button>
);

const FieldLabel = ({ children }) => (
  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{children}</span>
);

const SectionDivider = () => <div className="border-t border-slate-100" />;

const EditableChip = ({ value, variant = "purple", onChange, onRemove }) => {
  const cls = variant === "purple"
    ? "bg-purple-50 border-purple-100 text-purple-700"
    : "bg-slate-100 border-slate-200 text-slate-600";
  return (
    <div className={`flex items-center gap-1 pl-3 pr-1.5 py-1 rounded-full border ${cls}`}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs font-medium bg-transparent outline-none"
        style={{ width: `${Math.max(value.length, 4) + 1}ch` }}
      />
      <button onClick={onRemove} className="opacity-50 hover:opacity-100 hover:text-red-500 transition-all">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};

const AddChipBtn = ({ onClick, label = "+ Add" }) => (
  <button
    onClick={onClick}
    className="px-3 py-1 rounded-full border-2 border-dashed border-purple-100 text-xs text-purple-400 hover:border-purple-300 hover:text-purple-600 transition-colors"
  >
    {label}
  </button>
);

const ATTR_FIELDS = [
  { key: "material", label: "Material" },
  { key: "color", label: "Color" },
  { key: "pattern", label: "Pattern" },
  { key: "occasion", label: "Occasion" },
  { key: "category", label: "Category" },
  { key: "subcategory", label: "Subcategory" },
  { key: "package_contents", label: "Package Contents" },
];

const inputCls = "w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-all";

// ── My Listings sub-components ────────────────────────────────────────────────

// Module-level cache so fetched images survive tab switches
const imagesCache = {};

const CardImageThumb = ({ listingId }) => {
  const [imgSrc, setImgSrc] = useState(imagesCache[listingId]?.[0] ?? null);
  const [loading, setLoading] = useState(!(listingId in imagesCache));

  useEffect(() => {
    if (listingId in imagesCache) {
      setImgSrc(imagesCache[listingId][0] ?? null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    api.get(`/listing/${listingId}`)
      .then(({ data: res }) => {
        const imgs = res.data?.generated_images || [];
        imagesCache[listingId] = imgs;
        if (!cancelled) setImgSrc(imgs[0] ?? null);
      })
      .catch(() => { imagesCache[listingId] = []; })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [listingId]);

  if (loading) return <div className="w-12 h-12 rounded-xl bg-purple-50 animate-pulse shrink-0" />;
  if (!imgSrc) return null;
  return (
    <div className="w-12 h-12 rounded-xl overflow-hidden border border-slate-200 bg-slate-100 shrink-0">
      <img src={imgSrc} alt="product" className="w-full h-full object-cover" />
    </div>
  );
};

const CardSkeleton = () => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-pulse">
    <div className="p-4 space-y-3">
      <div className="h-4 bg-purple-50 rounded-xl w-3/4" />
      <div className="h-3 bg-purple-50 rounded-xl w-full" />
      <div className="flex gap-2">
        <div className="h-5 w-16 bg-purple-50 rounded-full" />
        <div className="h-5 w-12 bg-purple-50 rounded-full" />
      </div>
    </div>
    <div className="px-4 py-3 border-t border-slate-100 flex justify-between items-center">
      <div className="h-3 w-20 bg-purple-50 rounded-xl" />
      <div className="flex gap-2">
        <div className="h-7 w-14 bg-purple-50 rounded-xl" />
        <div className="h-7 w-14 bg-purple-50 rounded-xl" />
      </div>
    </div>
  </div>
);

const ListingCard = ({ listing, onEdit, onDelete, onClick }) => (
  <div
    onClick={onClick}
    className="cursor-pointer bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col group hover:shadow-md hover:border-purple-100 transition-all duration-200"
  >
    <div className="p-4 flex-1 space-y-2.5">
      <div className="flex items-start gap-3">
        {listing.image_count > 0 && <CardImageThumb listingId={listing._id} />}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 line-clamp-2 leading-snug">
            {listing.meesho_title || listing.flipkart_title || "Untitled listing"}
          </p>
          {listing.flipkart_title && listing.meesho_title && (
            <p className="text-xs text-slate-400 line-clamp-1 mt-0.5">{listing.flipkart_title}</p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 pt-0.5">
        {listing.category && (
          <span className="text-[10px] font-semibold px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full border border-purple-100">{listing.category}</span>
        )}
        {listing.color && (
          <span className="text-[10px] font-semibold px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">{listing.color}</span>
        )}
        {listing.pattern && (
          <span className="text-[10px] font-semibold px-2 py-0.5 bg-violet-50 text-violet-600 rounded-full border border-violet-100">{listing.pattern}</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[10px] text-slate-400 pt-0.5">
        <span>{(listing.seo_keywords || []).length} keywords</span>
        {listing.material && <span>· {listing.material}</span>}
      </div>
    </div>
    <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between">
      <span className="text-[10px] text-slate-400 font-medium">{fmt(listing.createdAt)}</span>
      <div className="flex items-center gap-1.5">
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(listing); }}
          className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-xl text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-100 transition-colors"
        >
          <Pencil className="w-3 h-3" /> Edit
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(listing._id); }}
          className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-xl text-red-600 bg-red-50 hover:bg-red-100 border border-red-100 transition-colors"
        >
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      </div>
    </div>
  </div>
);

const EditDialog = ({ listing, onClose, onSaved }) => {
  const [data, setData] = useState(() => ({ ...listing }));
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(listing.image_count > 0);

  useEffect(() => {
    if (!listing.image_count) return;
    api.get(`/listing/${listing._id}`)
      .then(({ data: res }) => setImages(res.data?.generated_images || []))
      .catch(() => {})
      .finally(() => setLoadingImages(false));
  }, [listing._id, listing.image_count]);

  const set = (key) => (val) => setData((p) => ({ ...p, [key]: val }));
  const setChip = (key, i) => (val) =>
    setData((p) => { const arr = [...(p[key] || [])]; arr[i] = val; return { ...p, [key]: arr }; });
  const removeChip = (key, i) =>
    setData((p) => ({ ...p, [key]: (p[key] || []).filter((_, idx) => idx !== i) }));
  const addChip = (key, placeholder) =>
    setData((p) => ({ ...p, [key]: [...(p[key] || []), placeholder] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: updated } = await api.put(`/listing/${data._id}`, data);
      toast.success("Listing updated!");
      onSaved(updated.data);
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update listing."));
    } finally {
      setSaving(false);
    }
  };

  const editInputCls = "w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-all";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b border-slate-100 shrink-0">
          <DialogTitle className="text-base font-bold text-slate-900">Edit Listing</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <FieldLabel>Meesho Title</FieldLabel>
              <span className="text-[10px] text-slate-400">{(data.meesho_title || "").length}/100</span>
            </div>
            <input type="text" maxLength={100} value={data.meesho_title || ""} onChange={(e) => set("meesho_title")(e.target.value)} className={editInputCls} />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <FieldLabel>Flipkart Title</FieldLabel>
              <span className="text-[10px] text-slate-400">{(data.flipkart_title || "").length}/120</span>
            </div>
            <input type="text" maxLength={120} value={data.flipkart_title || ""} onChange={(e) => set("flipkart_title")(e.target.value)} className={editInputCls} />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <FieldLabel>Description</FieldLabel>
              <span className="text-[10px] text-slate-400">
                {(data.description || "").trim().split(/\s+/).filter(Boolean).length} words
              </span>
            </div>
            <textarea rows={5} value={data.description || ""} onChange={(e) => set("description")(e.target.value)} className={`${editInputCls} resize-y leading-relaxed`} />
          </div>
          <div className="space-y-2">
            <FieldLabel>Features</FieldLabel>
            <div className="space-y-2">
              {(data.features || []).map((feat, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-purple-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <input type="text" value={feat} onChange={(e) => setChip("features", i)(e.target.value)} className={`flex-1 ${editInputCls}`} />
                  <button onClick={() => setData((p) => ({ ...p, features: (p.features || []).filter((_, idx) => idx !== i) }))} className="text-slate-300 hover:text-red-400 transition-colors shrink-0"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <button onClick={() => setData((p) => ({ ...p, features: [...(p.features || []), ""] }))} className="text-xs font-medium text-purple-600 hover:text-purple-800 transition-colors">+ Add feature</button>
            </div>
          </div>
          <div className="space-y-2">
            <FieldLabel>Attributes</FieldLabel>
            <dl className="grid grid-cols-2 gap-2.5">
              {ATTR_FIELDS.map(({ key, label }) => (
                <div key={key} className="flex flex-col gap-1 p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <dt className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</dt>
                  <input type="text" value={data[key] ?? ""} onChange={(e) => set(key)(e.target.value)} placeholder="—" className="text-sm font-medium text-slate-800 bg-transparent outline-none placeholder:text-slate-300" />
                </div>
              ))}
            </dl>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FieldLabel>SEO Keywords</FieldLabel>
              <span className="text-[10px] font-bold px-1.5 py-0.5 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-full">{(data.seo_keywords || []).length}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {(data.seo_keywords || []).map((kw, i) => (
                <EditableChip key={i} value={kw} variant="purple" onChange={setChip("seo_keywords", i)} onRemove={() => removeChip("seo_keywords", i)} />
              ))}
              <AddChipBtn onClick={() => addChip("seo_keywords", "keyword")} />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FieldLabel>Search Tags</FieldLabel>
              <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-full">{(data.search_tags || []).length}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {(data.search_tags || []).map((tag, i) => (
                <div key={i} className="flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-violet-50 border border-violet-100">
                  <Tag className="w-3 h-3 text-violet-400 shrink-0" />
                  <input type="text" value={tag} onChange={(e) => setChip("search_tags", i)(e.target.value)} className="text-xs font-medium text-violet-700 bg-transparent outline-none" style={{ width: `${Math.max(tag.length, 4) + 1}ch` }} />
                  <button onClick={() => removeChip("search_tags", i)} className="text-violet-300 hover:text-red-400 transition-colors"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <AddChipBtn onClick={() => addChip("search_tags", "tag")} label="+ Add tag" />
            </div>
          </div>
          {listing.image_count > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FieldLabel>AI Generated Images</FieldLabel>
                <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-full">
                  <Sparkles className="w-2.5 h-2.5" />{listing.image_count} images
                </span>
              </div>
              {loadingImages ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Array.from({ length: listing.image_count }).map((_, i) => (
                    <div key={i} className="aspect-square rounded-xl bg-purple-50 animate-pulse" />
                  ))}
                </div>
              ) : images.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {images.map((src, i) => (
                    <div key={i} className="relative group aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                      <img src={src} alt={`AI image ${i + 1}`} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-end justify-between p-2">
                        <span className="text-[10px] font-bold text-white opacity-0 group-hover:opacity-100 transition-opacity">
                          {["Front", "Back", "Angle / 3/4", "Detail / Close-up"][i] || `Image ${i + 1}`}
                        </span>
                        <a href={src} download={`ai-product-image-${i + 1}.png`} onClick={(e) => e.stopPropagation()} className="w-7 h-7 rounded-lg bg-white/90 backdrop-blur-sm flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity">
                          <Download className="w-3.5 h-3.5 text-slate-700" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400">Could not load images.</p>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="px-6 py-4 border-t border-slate-100 shrink-0">
          <Button variant="outline" onClick={onClose} disabled={saving} className="rounded-xl">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-xl bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white shadow-lg shadow-purple-600/25">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ViewDialog = ({ listing, onClose, onEdit }) => {
  const [images, setImages] = useState(imagesCache[listing._id] ?? null);
  const [loadingImages, setLoadingImages] = useState(!(listing._id in imagesCache) && listing.image_count > 0);
  const { copy, isCopied } = useCopy();

  useEffect(() => {
    if (listing._id in imagesCache || !listing.image_count) {
      setImages(imagesCache[listing._id] ?? []);
      setLoadingImages(false);
      return;
    }
    api.get(`/listing/${listing._id}`)
      .then(({ data: res }) => {
        const imgs = res.data?.generated_images || [];
        imagesCache[listing._id] = imgs;
        setImages(imgs);
      })
      .catch(() => setImages([]))
      .finally(() => setLoadingImages(false));
  }, [listing._id, listing.image_count]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b border-slate-100 shrink-0">
          <DialogTitle className="text-base font-bold text-slate-900 line-clamp-1 pr-8">
            {listing.meesho_title || listing.flipkart_title || "Listing Details"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Images */}
          {listing.image_count > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FieldLabel>AI Generated Images</FieldLabel>
                <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-full">
                  <Sparkles className="w-2.5 h-2.5" />{listing.image_count} images
                </span>
              </div>
              {loadingImages ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Array.from({ length: listing.image_count }).map((_, i) => (
                    <div key={i} className="aspect-square bg-purple-50 animate-pulse rounded-xl" />
                  ))}
                </div>
              ) : (images || []).length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {(images || []).map((src, i) => (
                    <div key={i} className="relative group aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                      <img src={src} alt={`AI image ${i + 1}`} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-end justify-between p-2">
                        <span className="text-[10px] font-bold text-white opacity-0 group-hover:opacity-100 transition-opacity">
                          {["Front", "Back", "Angle / 3/4", "Detail / Close-up"][i] || `Image ${i + 1}`}
                        </span>
                        <a href={src} download={`ai-product-image-${i + 1}.png`} onClick={(e) => e.stopPropagation()} className="w-7 h-7 rounded-lg bg-white/90 backdrop-blur-sm flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity" title="Download">
                          <Download className="w-3.5 h-3.5 text-slate-700" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}

          {/* Meesho Title */}
          {listing.meesho_title && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FieldLabel>Meesho Title</FieldLabel>
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-pink-100 text-pink-700 rounded-full">max 100</span>
                </div>
                <CopyBtn text={listing.meesho_title} id="v-meesho" copy={copy} isCopied={isCopied} />
              </div>
              <p className="text-sm text-slate-800 px-4 py-3 rounded-xl bg-slate-50 border border-slate-100 leading-snug">{listing.meesho_title}</p>
            </div>
          )}

          {/* Flipkart Title */}
          {listing.flipkart_title && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FieldLabel>Flipkart Title</FieldLabel>
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">max 120</span>
                </div>
                <CopyBtn text={listing.flipkart_title} id="v-flipkart" copy={copy} isCopied={isCopied} />
              </div>
              <p className="text-sm text-slate-800 px-4 py-3 rounded-xl bg-slate-50 border border-slate-100 leading-snug">{listing.flipkart_title}</p>
            </div>
          )}

          <SectionDivider />

          {/* Description */}
          {listing.description && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <FieldLabel>Description</FieldLabel>
                <CopyBtn text={listing.description} id="v-desc" copy={copy} isCopied={isCopied} />
              </div>
              <p className="text-sm text-slate-700 px-4 py-3 rounded-xl bg-slate-50 border border-slate-100 leading-relaxed whitespace-pre-wrap">{listing.description}</p>
            </div>
          )}

          {/* Features */}
          {(listing.features || []).length > 0 && (
            <div className="space-y-2">
              <FieldLabel>Features</FieldLabel>
              <ul className="space-y-1.5">
                {listing.features.map((feat, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-purple-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                    <span className="text-sm text-slate-700 leading-snug">{feat}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Attributes */}
          {ATTR_FIELDS.some(({ key }) => listing[key]) && (
            <>
              <SectionDivider />
              <div className="space-y-2">
                <FieldLabel>Attributes</FieldLabel>
                <dl className="grid grid-cols-2 gap-2.5">
                  {ATTR_FIELDS.filter(({ key }) => listing[key]).map(({ key, label }) => (
                    <div key={key} className="flex flex-col gap-1 p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <dt className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</dt>
                      <dd className="text-sm font-medium text-slate-800">{listing[key]}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </>
          )}

          {/* SEO Keywords */}
          {(listing.seo_keywords || []).length > 0 && (
            <>
              <SectionDivider />
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FieldLabel>SEO Keywords</FieldLabel>
                    <span className="text-[10px] font-bold px-1.5 py-0.5 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-full">{listing.seo_keywords.length}</span>
                  </div>
                  <CopyBtn text={listing.seo_keywords.join(", ")} id="v-seo" copy={copy} isCopied={isCopied} />
                </div>
                <div className="flex flex-wrap gap-2">
                  {listing.seo_keywords.map((kw, i) => (
                    <span key={i} className="text-xs font-medium px-3 py-1 rounded-full bg-purple-50 border border-purple-100 text-purple-700">{kw}</span>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Search Tags */}
          {(listing.search_tags || []).length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FieldLabel>Search Tags</FieldLabel>
                <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-full">{listing.search_tags.length}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {listing.search_tags.map((tag, i) => (
                  <span key={i} className="flex items-center gap-1.5 pl-2.5 pr-2.5 py-1 rounded-full bg-violet-50 border border-violet-100 text-xs font-medium text-violet-700">
                    <Tag className="w-3 h-3 text-violet-400 shrink-0" />{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-100 shrink-0">
          <span className="text-xs text-slate-400 mr-auto self-center">{fmt(listing.createdAt)}</span>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Close</Button>
          <Button
            onClick={() => { onClose(); onEdit(listing); }}
            className="rounded-xl bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white shadow-lg shadow-purple-600/25"
          >
            <Pencil className="w-4 h-4 mr-2" /> Edit Listing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── main page ─────────────────────────────────────────────────────────────────
export const ListingGeneratorPage = () => {
  const [activeTab, setActiveTab] = useState("generate");

  // Generate tab
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [withImages, setWithImages] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [regenerating, setRegenerating] = useState(null);
  const [error, setError] = useState(null);
  const [listing, setListing] = useState(null);
  const [displayed, setDisplayed] = useState(null);

  const { copy, isCopied } = useCopy();

  // My Listings tab
  const [mlListings, setMlListings] = useState([]);
  const [mlPagination, setMlPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [mlLoading, setMlLoading] = useState(false);
  const [viewingListing, setViewingListing] = useState(null);
  const [editingListing, setEditingListing] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ── file handling ─────────────────────────────────────────────────────────
  const addFiles = useCallback((files) => {
    const valid = Array.from(files).filter((f) => ACCEPTED.includes(f.type));
    if (valid.length < files.length) toast.warning("Some files skipped — only JPG, PNG, WEBP are supported.");
    if (!valid.length) return;
    setImages((p) => [...p, ...valid]);
    valid.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => setPreviews((p) => [...p, e.target.result]);
      reader.readAsDataURL(file);
    });
  }, []);

  const removeImage = (i) => {
    setImages((p) => p.filter((_, idx) => idx !== i));
    setPreviews((p) => p.filter((_, idx) => idx !== i));
  };

  // ── API ───────────────────────────────────────────────────────────────────
  const callApi = async (section = null) => {
    const form = new FormData();
    images.forEach((file) => form.append("images[]", file));
    if (section) {
      form.append("section", section);
    } else {
      form.append("withImages", withImages ? "true" : "false");
    }
    const { data } = await api.post("/listing/generate-listing", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.data;
  };

  const startAnimation = useCallback((fullData) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setDisplayed({});
    setAnimating(true);
    animateFields(fullData, (partial) => setDisplayed({ ...partial }), controller.signal).then(() => {
      if (!controller.signal.aborted) {
        setDisplayed(fullData);
        setAnimating(false);
      }
    });
  }, []);

  const handleGenerate = async () => {
    if (!images.length) { toast.error("Upload at least one product image."); return; }
    setError(null);
    setListing(null);
    setDisplayed(null);
    setIsGenerating(true);
    try {
      const data = await callApi();
      setListing(data);
      startAnimation(data);
      toast.success("Listing generated!");
    } catch (err) {
      const msg = getErrorMessage(err, "Failed to generate listing.");
      setError(msg);
      toast.error(msg);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerate = async (section) => {
    if (!images.length) return;
    setRegenerating(section);
    try {
      const result = await callApi(section);
      const updated = { ...listing, ...result };
      setListing(updated);
      setDisplayed((p) => ({ ...p, ...result }));
      toast.success(`${section === "seo" ? "SEO & tags" : "Description"} regenerated!`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Regeneration failed."));
    } finally {
      setRegenerating(null);
    }
  };

  const setField = (key) => (val) => setDisplayed((p) => ({ ...p, [key]: val }));
  const setChip = (key, i) => (val) =>
    setDisplayed((p) => { const arr = [...(p[key] || [])]; arr[i] = val; return { ...p, [key]: arr }; });
  const removeChip = (key, i) =>
    setDisplayed((p) => ({ ...p, [key]: (p[key] || []).filter((_, idx) => idx !== i) }));
  const addChip = (key, placeholder) =>
    setDisplayed((p) => ({ ...p, [key]: [...(p[key] || []), placeholder] }));

  const copyAll = () => {
    if (!displayed) return;
    const lines = [
      `MEESHO TITLE:\n${displayed.meesho_title || ""}`,
      `\nFLIPKART TITLE:\n${displayed.flipkart_title || ""}`,
      `\nDESCRIPTION:\n${displayed.description || ""}`,
      `\nFEATURES:\n${(displayed.features || []).map((f) => `• ${f}`).join("\n")}`,
      `\nMATERIAL: ${displayed.material || "—"}`,
      `COLOR: ${displayed.color || "—"}`,
      `PATTERN: ${displayed.pattern || "—"}`,
      `OCCASION: ${displayed.occasion || "—"}`,
      `CATEGORY: ${displayed.category || "—"}`,
      `SUBCATEGORY: ${displayed.subcategory || "—"}`,
      `PACKAGE CONTENTS: ${displayed.package_contents || "—"}`,
      `\nSEO KEYWORDS:\n${(displayed.seo_keywords || []).join(", ")}`,
      `\nSEARCH TAGS:\n${(displayed.search_tags || []).join(", ")}`,
    ].join("\n");
    copy(lines, "all");
    toast.success("Full listing copied!");
  };

  const exportJSON = () => {
    if (!displayed) return;
    const blob = new Blob([JSON.stringify(displayed, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `listing-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported as JSON.");
  };

  // ── My Listings ───────────────────────────────────────────────────────────
  const fetchListings = useCallback(async (page = 1) => {
    setMlLoading(true);
    try {
      const { data } = await api.get(`/listing/my-listings?page=${page}&limit=${PAGE_SIZE}`);
      setMlListings(data.data);
      setMlPagination(data.pagination);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to load listings."));
    } finally {
      setMlLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "my-listings") fetchListings(1);
  }, [activeTab, fetchListings]);

  const handleSaved = (updated) => {
    setMlListings((prev) =>
      prev.map((l) => l._id === updated._id ? { ...updated, image_count: l.image_count } : l)
    );
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    setIsDeleting(true);
    try {
      await api.delete(`/listing/${deletingId}`);
      setMlListings((prev) => prev.filter((l) => l._id !== deletingId));
      setMlPagination((p) => ({ ...p, total: p.total - 1 }));
      toast.success("Listing deleted.");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to delete listing."));
    } finally {
      setIsDeleting(false);
      setDeletingId(null);
    }
  };

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5" data-testid="panel-listing-generator">

      {/* Tab bar */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-2xl w-fit">
        {[
          { id: "generate", label: "AI Generator", Icon: Wand2 },
          { id: "my-listings", label: "My Listings", Icon: LayoutList },
        ].map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
              activeTab === id
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ── GENERATE TAB ─────────────────────────────────────────────────── */}
      {activeTab === "generate" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

          {/* LEFT: Upload form */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-800">Upload Images</span>
              <div className="flex gap-1.5">
                {["JPG", "PNG", "WEBP"].map((ext) => (
                  <span key={ext} className="text-[10px] font-bold px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full">{ext}</span>
                ))}
              </div>
            </div>

            <div className="p-5 space-y-4">
              <div
                onDrop={(e) => { e.preventDefault(); setIsDragging(false); addFiles(e.dataTransfer.files); }}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onClick={() => fileInputRef.current?.click()}
                className={`relative flex flex-col items-center justify-center gap-3 min-h-[160px] rounded-2xl border-2 border-dashed cursor-pointer transition-all duration-200 ${
                  isDragging
                    ? "border-purple-400 bg-purple-50/60 scale-[1.01]"
                    : "border-slate-200 hover:border-purple-300 hover:bg-purple-50/30 bg-slate-50/40"
                }`}
              >
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors ${isDragging ? "bg-purple-100 text-purple-600" : "bg-slate-100 text-slate-400"}`}>
                  <ImagePlus className="w-6 h-6" />
                </div>
                <div className="text-center pointer-events-none">
                  <p className="text-sm font-semibold text-slate-700">{isDragging ? "Drop images here" : "Drag & drop product images"}</p>
                  <p className="text-xs text-slate-400 mt-1">or click to browse · JPG, PNG, WEBP · up to 10 MB each</p>
                </div>
                <input ref={fileInputRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => addFiles(e.target.files)} />
              </div>

              {previews.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  {previews.map((src, i) => (
                    <div key={i} className="relative group w-20 h-20 rounded-xl overflow-hidden border border-slate-200 shadow-sm">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      <button onClick={(e) => { e.stopPropagation(); removeImage(i); }} className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="w-5 h-5 text-white" />
                      </button>
                    </div>
                  ))}
                  <button onClick={() => fileInputRef.current?.click()} className="w-20 h-20 rounded-xl border-2 border-dashed border-slate-200 hover:border-purple-300 flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-purple-500 transition-colors">
                    <Upload className="w-4 h-4" />
                    <span className="text-[10px] font-medium">Add more</span>
                  </button>
                </div>
              )}
            </div>

            <div className="px-5 pb-4">
              <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${withImages ? "border-purple-400 bg-purple-50/60" : "border-slate-200 hover:border-purple-200 hover:bg-purple-50/20"}`}>
                <input type="checkbox" checked={withImages} onChange={(e) => setWithImages(e.target.checked)} className="mt-0.5 w-4 h-4 rounded accent-purple-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                    <Camera className="w-4 h-4 text-purple-600" />
                    Generate AI Product Images
                  </span>
                  <p className="text-xs text-slate-500 mt-1">
                    AI generates 4 studio-quality images. For clothing, images automatically show a fashion model wearing the item.
                  </p>
                </div>
              </label>
            </div>

            <div className="px-5 pb-5">
              <button
                onClick={handleGenerate}
                disabled={isGenerating || !images.length}
                className="w-full h-12 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 shadow-lg shadow-purple-600/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {withImages ? "Generating listing + AI images… (~60s)" : "Analyzing & generating listing…"}
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5" />
                    Generate Listing
                  </>
                )}
              </button>
            </div>

            {error && !isGenerating && (
              <div className="mx-5 mb-5 flex items-start gap-3 p-4 rounded-2xl bg-red-50 border border-red-200 text-red-700">
                <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">Generation failed</p>
                  <p className="text-xs text-red-500 mt-0.5 break-words">{error}</p>
                </div>
                <button onClick={handleGenerate} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl border border-red-200 hover:bg-red-100 transition-colors shrink-0">
                  <RotateCcw className="w-3.5 h-3.5" /> Retry
                </button>
              </div>
            )}
          </div>

          {/* RIGHT: Generated output */}
          <div className="lg:sticky lg:top-6">
            {isGenerating ? (
              <ListingSkeleton showImageSkeleton={withImages} />
            ) : displayed !== null ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 bg-slate-50/60">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full transition-colors ${animating ? "bg-amber-400 animate-pulse" : "bg-green-500"}`} />
                    <span className="text-sm font-semibold text-slate-800">
                      {animating ? "Generating…" : "Generated Listing"}
                    </span>
                  </div>
                  {!animating && (
                    <button onClick={copyAll} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl bg-purple-50 border border-purple-100 text-purple-700 hover:bg-purple-100 transition-colors">
                      {isCopied("all") ? <Check className="w-3.5 h-3.5" /> : <ClipboardCopy className="w-3.5 h-3.5" />}
                      Copy All
                    </button>
                  )}
                </div>

                <div className="p-5 space-y-6">

                  {/* AI Generated Images — top */}
                  {(displayed.generated_images || []).length > 0 && (
                    <>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FieldLabel>AI Generated Images</FieldLabel>
                            <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-full">
                              <Sparkles className="w-2.5 h-2.5" />{displayed.generated_images.length} images
                            </span>
                          </div>
                          <span className="text-[10px] text-slate-400">Hover to download</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {displayed.generated_images.map((src, i) => {
                            const isModel = displayed.image_type === "model";
                            const productLabels = ["Front", "Back", "Angle", "Detail"];
                            const modelLabels   = ["Front", "Back", "3/4 View", "Close-up"];
                            const label = isModel ? (modelLabels[i] || `Look ${i + 1}`) : (productLabels[i] || `Image ${i + 1}`);
                            return (
                              <div key={i} className="relative group aspect-square rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-slate-50">
                                <img src={src} alt={label} className="w-full h-full object-cover" />
                                {isModel && (
                                  <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-600/90 backdrop-blur-sm">
                                    <Sparkles className="w-2.5 h-2.5 text-white" />
                                    <span className="text-[9px] font-bold text-white">Model</span>
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200 flex items-end justify-between p-2">
                                  <span className="text-[10px] font-bold text-white opacity-0 group-hover:opacity-100 transition-opacity">{label}</span>
                                  <a href={src} download={`ai-product-image-${i + 1}.png`} onClick={(e) => e.stopPropagation()} className="w-7 h-7 rounded-lg bg-white/90 backdrop-blur-sm flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity" title="Download">
                                    <Download className="w-3.5 h-3.5 text-slate-700" />
                                  </a>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <SectionDivider />
                    </>
                  )}

                  {/* Meesho Title */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FieldLabel>Meesho Title</FieldLabel>
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-pink-100 text-pink-700 rounded-full">max 100</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-400">{(displayed.meesho_title || "").length}/100</span>
                        {!animating && <CopyBtn text={displayed.meesho_title || ""} id="meesho" copy={copy} isCopied={isCopied} />}
                      </div>
                    </div>
                    <input
                      type="text"
                      maxLength={100}
                      value={displayed.meesho_title || ""}
                      onChange={(e) => setField("meesho_title")(e.target.value)}
                      readOnly={animating}
                      className={`${inputCls} ${animating ? "bg-slate-50/60 caret-transparent" : ""}`}
                    />
                  </div>

                  {/* Flipkart Title */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FieldLabel>Flipkart Title</FieldLabel>
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">max 120</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-400">{(displayed.flipkart_title || "").length}/120</span>
                        {!animating && <CopyBtn text={displayed.flipkart_title || ""} id="flipkart" copy={copy} isCopied={isCopied} />}
                      </div>
                    </div>
                    <input
                      type="text"
                      maxLength={120}
                      value={displayed.flipkart_title || ""}
                      onChange={(e) => setField("flipkart_title")(e.target.value)}
                      readOnly={animating}
                      className={`${inputCls} ${animating ? "bg-slate-50/60 caret-transparent" : ""}`}
                    />
                  </div>

                  <SectionDivider />

                  {/* Description */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <FieldLabel>Description</FieldLabel>
                        <span className="text-[10px] text-slate-400">
                          {(displayed.description || "").trim().split(/\s+/).filter(Boolean).length} words
                        </span>
                      </div>
                      {!animating && (
                        <div className="flex items-center gap-1">
                          <CopyBtn text={displayed.description || ""} id="desc" copy={copy} isCopied={isCopied} />
                          <RegenBtn label="Regenerate" loading={regenerating === "description"} onClick={() => handleRegenerate("description")} />
                        </div>
                      )}
                    </div>
                    <textarea
                      rows={6}
                      value={displayed.description || ""}
                      onChange={(e) => setField("description")(e.target.value)}
                      readOnly={animating}
                      className={`${inputCls} resize-y leading-relaxed ${animating ? "bg-slate-50/60 caret-transparent" : ""}`}
                    />
                  </div>

                  {/* Features */}
                  {(displayed.features || []).length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FieldLabel>Features</FieldLabel>
                        {!animating && (
                          <CopyBtn text={(displayed.features || []).map((f) => `• ${f}`).join("\n")} id="features" copy={copy} isCopied={isCopied} />
                        )}
                      </div>
                      <div className="space-y-2">
                        {(displayed.features || []).map((feat, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-purple-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                            <input
                              type="text"
                              value={feat}
                              onChange={(e) => setChip("features", i)(e.target.value)}
                              readOnly={animating}
                              className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-all"
                            />
                            {!animating && (
                              <button onClick={() => removeChip("features", i)} className="text-slate-300 hover:text-red-400 transition-colors shrink-0">
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                        {!animating && (
                          <button onClick={() => addChip("features", "New feature")} className="text-xs font-medium text-purple-600 hover:text-purple-800 transition-colors">
                            + Add feature
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Attributes */}
                  {"material" in displayed && (
                    <>
                      <SectionDivider />
                      <div className="space-y-3">
                        <FieldLabel>Attributes</FieldLabel>
                        <dl className="grid grid-cols-2 gap-3">
                          {ATTR_FIELDS.map(({ key, label }) => (
                            <div key={key} className="flex flex-col gap-1 p-3 rounded-xl bg-slate-50 border border-slate-100 hover:border-purple-100 transition-colors">
                              <dt className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</dt>
                              <input
                                type="text"
                                value={displayed[key] ?? ""}
                                onChange={(e) => setField(key)(e.target.value)}
                                readOnly={animating}
                                placeholder="—"
                                className="text-sm font-medium text-slate-800 bg-transparent outline-none placeholder:text-slate-300"
                              />
                            </div>
                          ))}
                        </dl>
                      </div>
                    </>
                  )}

                  {/* SEO Keywords */}
                  {(displayed.seo_keywords || []).length > 0 && (
                    <>
                      <SectionDivider />
                      <div className="space-y-3">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            <FieldLabel>SEO Keywords</FieldLabel>
                            <span className="text-[10px] font-bold px-1.5 py-0.5 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-full">
                              {(displayed.seo_keywords || []).length}
                            </span>
                          </div>
                          {!animating && (
                            <div className="flex items-center gap-1">
                              <CopyBtn text={(displayed.seo_keywords || []).join(", ")} id="seo" copy={copy} isCopied={isCopied} />
                              <RegenBtn label="Regenerate SEO" loading={regenerating === "seo"} onClick={() => handleRegenerate("seo")} />
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {(displayed.seo_keywords || []).map((kw, i) =>
                            animating ? (
                              <span key={i} className="text-xs font-medium px-3 py-1 rounded-full bg-purple-50 border border-purple-100 text-purple-700">{kw}</span>
                            ) : (
                              <EditableChip key={i} value={kw} variant="purple" onChange={setChip("seo_keywords", i)} onRemove={() => removeChip("seo_keywords", i)} />
                            )
                          )}
                          {!animating && <AddChipBtn onClick={() => addChip("seo_keywords", "keyword")} />}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Search Tags */}
                  {(displayed.search_tags || []).length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FieldLabel>Search Tags</FieldLabel>
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-full">
                            {(displayed.search_tags || []).length}
                          </span>
                        </div>
                        {!animating && <CopyBtn text={(displayed.search_tags || []).join(", ")} id="tags" copy={copy} isCopied={isCopied} />}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(displayed.search_tags || []).map((tag, i) =>
                          animating ? (
                            <span key={i} className="flex items-center gap-1.5 pl-2.5 pr-2.5 py-1 rounded-full bg-violet-50 border border-violet-100 text-xs font-medium text-violet-700">
                              <Tag className="w-3 h-3 text-violet-400 shrink-0" />{tag}
                            </span>
                          ) : (
                            <div key={i} className="flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-violet-50 border border-violet-100">
                              <Tag className="w-3 h-3 text-violet-400 shrink-0" />
                              <input type="text" value={tag} onChange={(e) => setChip("search_tags", i)(e.target.value)} className="text-xs font-medium text-violet-700 bg-transparent outline-none" style={{ width: `${Math.max(tag.length, 4) + 1}ch` }} />
                              <button onClick={() => removeChip("search_tags", i)} className="text-violet-300 hover:text-red-400 transition-colors"><X className="w-3 h-3" /></button>
                            </div>
                          )
                        )}
                        {!animating && <AddChipBtn onClick={() => addChip("search_tags", "tag")} label="+ Add tag" />}
                      </div>
                    </div>
                  )}

                  {/* Action buttons */}
                  {!animating && (
                    <>
                      <SectionDivider />
                      <div className="flex flex-wrap gap-3 pt-1">
                        <button onClick={copyAll} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-purple-50 border border-purple-100 text-purple-700 hover:bg-purple-100 text-sm font-semibold transition-colors">
                          {isCopied("all") ? <Check className="w-4 h-4" /> : <ClipboardCopy className="w-4 h-4" />}
                          Copy All
                        </button>
                        <button onClick={exportJSON} className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-semibold transition-colors">
                          <Download className="w-4 h-4" />
                          Export JSON
                        </button>
                      </div>
                    </>
                  )}

                </div>
              </div>
            ) : (
              /* Empty right-panel state */
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center justify-center gap-4 py-20 text-center px-6 min-h-[400px]">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-50 to-violet-100 flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-purple-400" />
                </div>
                <div>
                  <p className="text-base font-bold text-slate-800">Your listing will appear here</p>
                  <p className="text-sm text-slate-500 mt-1 max-w-xs">Upload product images and click Generate to see AI-powered listing content stream in real time.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MY LISTINGS TAB ─────────────────────────────────────────────────── */}
      {activeTab === "my-listings" && (
        <div className="space-y-6">
          {mlLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
            </div>
          ) : mlListings.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center justify-center gap-4 py-20 text-center px-6">
              <div className="w-16 h-16 rounded-2xl bg-purple-50 text-purple-400 flex items-center justify-center">
                <PackageSearch className="w-8 h-8" />
              </div>
              <div>
                <p className="text-base font-bold text-slate-800">No listings yet</p>
                <p className="text-sm text-slate-500 mt-1">
                  Generate your first product listing using the{" "}
                  <button onClick={() => setActiveTab("generate")} className="text-purple-600 font-semibold hover:underline">
                    AI Generator
                  </button>.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {mlListings.map((l) => (
                <ListingCard key={l._id} listing={l} onEdit={setEditingListing} onDelete={setDeletingId} onClick={() => setViewingListing(l)} />
              ))}
            </div>
          )}

          {!mlLoading && mlPagination.pages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={mlPagination.page <= 1}
                onClick={() => fetchListings(mlPagination.page - 1)}
                className="rounded-xl border-slate-200 text-slate-600 hover:bg-purple-50 hover:border-purple-200 hover:text-purple-700"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <span className="text-sm text-slate-500 font-medium">
                Page {mlPagination.page} of {mlPagination.pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={mlPagination.page >= mlPagination.pages}
                onClick={() => fetchListings(mlPagination.page + 1)}
                className="rounded-xl border-slate-200 text-slate-600 hover:bg-purple-50 hover:border-purple-200 hover:text-purple-700"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* View dialog */}
      {viewingListing && (
        <ViewDialog
          listing={viewingListing}
          onClose={() => setViewingListing(null)}
          onEdit={(l) => { setViewingListing(null); setEditingListing(l); }}
        />
      )}

      {/* Edit dialog */}
      {editingListing && (
        <EditDialog listing={editingListing} onClose={() => setEditingListing(null)} onSaved={handleSaved} />
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete listing?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The listing will be permanently removed from your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting} className="bg-red-600 hover:bg-red-700 focus:ring-red-600">
              {isDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {isDeleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
